<script setup lang="ts">
import { type Ref, toValue } from 'vue';
import { $t } from '@/locales';
import type { NaiveUI } from '@/typings/naive-ui';

type TableOperation = 'view' | 'delete' | 'refresh' | 'import' | 'export' | string;

defineOptions({
  name: 'TableHeaderOperation'
});

interface Props {
  itemAlign?: NaiveUI.Align;
  disabledDelete?: boolean;
  loading?: boolean;
  /**
   * 按钮loading状态
   *
   * @param key 按钮的 key
   */
  buttonLoading?: (key: string) => Ref<boolean> | boolean;
  tableOperation?: TableOperation[];
}

defineProps<Props>();

interface Emits {
  (e: 'add'): void;
  (e: 'delete'): void;
  (e: 'refresh'): void;
  (e: 'import'): void;
  (e: 'export'): void;
}

const emit = defineEmits<Emits>();

const columns = defineModel<NaiveUI.TableColumnCheck[]>('columns', {
  default: () => []
});

function add() {
  emit('add');
}

function batchDelete() {
  emit('delete');
}

function refresh() {
  emit('refresh');
}

function handleImport() {
  emit('import');
}

function handleExport() {
  emit('export');
}
</script>

<template>
  <NSpace :align="itemAlign" wrap justify="end" class="lt-sm:w-200px">
    <slot name="prefix"></slot>
    <NButton
      v-if="tableOperation?.includes('add')"
      key="table-add-btn"
      size="small"
      ghost
      type="primary"
      :loading="toValue(buttonLoading?.('add')) ?? false"
      @click="add"
    >
      <template #icon>
        <icon-ic-round-plus class="text-icon" />
      </template>
      {{ $t('common.add') }}
    </NButton>
    <NPopconfirm v-if="tableOperation?.includes('delete')" @positive-click="batchDelete">
      <template #trigger>
        <NButton
          key="table-batch_delete-btn"
          size="small"
          ghost
          type="error"
          :disabled="disabledDelete"
          :loading="toValue(buttonLoading?.('delete')) ?? false"
        >
          <template #icon>
            <icon-ic-round-delete class="text-icon" />
          </template>
          {{ $t('common.batchDelete') }}
        </NButton>
      </template>
      {{ $t('common.confirmDelete') }}
    </NPopconfirm>
    <NButton
      v-if="tableOperation?.includes('import')"
      key="table-import-btn"
      size="small"
      type="success"
      :loading="toValue(buttonLoading?.('import')) ?? false"
      @click="handleImport"
    >
      <template #icon>
        <icon-solar-import-outline class="text-icon" />
      </template>
      {{ $t('common.handleImport') }}
    </NButton>
    <NButton
      v-if="tableOperation?.includes('export')"
      key="table-export-btn"
      size="small"
      type="success"
      :loading="toValue(buttonLoading?.('export')) ?? false"
      @click="handleExport"
    >
      <template #icon>
        <icon-solar-export-outline class="text-icon" />
      </template>
      {{ $t('common.handleExport') }}
    </NButton>
    <slot name="default"></slot>
    <NButton v-if="tableOperation?.includes('refresh')" size="small" @click="refresh">
      <template #icon>
        <icon-mdi-refresh class="text-icon" :class="{ 'animate-spin': loading }" />
      </template>
      {{ $t('common.refresh') }}
    </NButton>
    <TableColumnSetting v-model:columns="columns" />
    <slot name="suffix"></slot>
  </NSpace>
</template>

<style scoped></style>
